import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalTitle, 
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Select,
  Alert
} from '../../styles/components';
import { SecretDisplayModal } from '../common/SecretDisplayModal';
import {
  useGetClientQuery,
  useSetClientEndpointMutation,
  useSetClientUsernameMutation,
  useSetClientCustomerIdMutation,
  useSetClientPasswordMutation,
  useSetCertificateMutation,
  useCycleClientSecretMutation,
  useSetClientApiKeyMutation,
  useDeleteClientMutation,
  useSetClientSettingsMutation
} from '../../store/api/clientsApi';
import { useGetAllEndpointsQuery } from '../../store/api/endpointsApi';
import { useGetProvidersQuery } from '../../store/api/providersApi';
import { useGetOrganizationsQuery } from '../../store/api/organizationsApi';
import { AuthType } from '../../types/api';
import { useNotifications } from '../../hooks/useNotifications';
import { usePermissions } from '../../hooks/usePermissions';
import { validationPatterns } from '../../utils/validation';
import {
  XMarkIcon,
  PencilIcon,
  KeyIcon,
  DocumentArrowUpIcon,
  CheckIcon,
  XCircleIcon,
  BuildingLibraryIcon,
  UserIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  TrashIcon,
  ServerIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

interface ClientEditModalProps {
  clientId: string;
  onClose: () => void;
  onClientDeleted?: () => void;
}

interface EditableField {
  field: 'endpoint' | 'username' | 'password' | 'certificate';
  value: string | File | null;
  originalValue: string | null;
}


const EditSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.lg};
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const EditableFieldContainer = styled.div`
  display: flex;
  align-items: end;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const EditButton = styled(Button)`
  min-width: auto;
  height: 2.25rem;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClickableField = styled.div`
  height: 2.25rem;
  padding: 0 0.75rem;
  background: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.gray[500]};
  font-style: italic;
  cursor: pointer;
  border: 1px dashed ${props => props.theme.colors.gray[300]};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  font-size: 0.875rem;
  
  &:hover {
    background: ${props => props.theme.colors.gray[100]};
    border-color: ${props => props.theme.colors.gray[400]};
  }
`;

const DisplayField = styled.div`
  height: 2.25rem;
  padding: 0 0.75rem;
  background: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.gray[700]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  font-size: 0.875rem;
`;

const FieldContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldLabel = styled(Label)`
  margin-bottom: ${props => props.theme.spacing.xs};
  display: block;
`;

const EditControls = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  margin-left: ${props => props.theme.spacing.sm};
`;

const FileUploadArea = styled.div<{ $isDragOver: boolean }>`
  border: 2px dashed ${props => props.$isDragOver 
    ? props.theme.colors.primary[400] 
    : props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  background-color: ${props => props.$isDragOver 
    ? props.theme.colors.primary[50] 
    : props.theme.colors.gray[50]};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary[400]};
    background-color: ${props => props.theme.colors.primary[50]};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
`;

const CollapsibleHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.lg};
  background-color: ${props => props.$isExpanded ? props.theme.colors.gray[50] : 'white'};
  transition: all 0.2s ease;
  margin-bottom: ${props => props.$isExpanded ? props.theme.spacing.md : '0'};

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }
`;

const CollapsibleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex: 1;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary[600]};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.gray[900]};
    margin: 0;
  }
`;

const StatusBadge = styled.span<{ $variant: 'disabled' | 'active' }>`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props =>
    props.$variant === 'disabled'
      ? props.theme.colors.gray[100]
      : props.theme.colors.primary[100]};
  color: ${props =>
    props.$variant === 'disabled'
      ? props.theme.colors.gray[600]
      : props.theme.colors.primary[700]};
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.gray[400]};

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const CollapsibleContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borders.radius.lg} ${props => props.theme.borders.radius.lg};
  margin-top: -${props => props.theme.spacing.md};
`;

const IPListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const IPItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.gray[50]};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-family: monospace;
  font-size: ${props => props.theme.fontSizes.sm};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${props => props.theme.colors.error[600]};
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borders.radius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.error[50]};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const AddIPContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ErrorText = styled.div`
  color: ${props => props.theme.colors.error[600]};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.gray[500]};
  font-size: ${props => props.theme.fontSizes.sm};
`;


export function ClientEditModal({ clientId, onClose, onClientDeleted }: ClientEditModalProps) {
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>({});
  const [editValues, setEditValues] = useState<Record<string, string | File | null>>({});
  const [certificatePassword, setCertificatePassword] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [generatedSecret, setGeneratedSecret] = useState<string>('');
  const [showSecretModal, setShowSecretModal] = useState(false);

  // IP Whitelist state
  const [isIPSectionExpanded, setIsIPSectionExpanded] = useState(false);
  const [ipAddresses, setIpAddresses] = useState<string[]>([]);
  const [newIPAddress, setNewIPAddress] = useState('');
  const [ipError, setIpError] = useState<string>('');

  const { notify } = useNotifications();
  const { isSupport } = usePermissions();
  const { data: client, isLoading: clientLoading, refetch: refetchClient } = useGetClientQuery(clientId);
  const { data: endpoints } = useGetAllEndpointsQuery(); // Returns string[] of bank names
  const { data: providers } = useGetProvidersQuery();
  const { data: orgsData, isLoading: orgsLoading } = useGetOrganizationsQuery({ page: 1, pageSize: 9999 });
  const organization = orgsData?.items?.find(org =>
    org.organizationId === client?.organizationId || org.id === client?.organizationId
  );
  
  const [setEndpoint, { isLoading: isSettingEndpoint }] = useSetClientEndpointMutation();
  const [setUsername, { isLoading: isSettingUsername }] = useSetClientUsernameMutation();
  const [setCustomerId, { isLoading: isSettingCustomerId }] = useSetClientCustomerIdMutation();
  const [setPassword, { isLoading: isSettingPassword }] = useSetClientPasswordMutation();
  const [setCertificate, { isLoading: isSettingCertificate }] = useSetCertificateMutation();
  const [setApiKey, { isLoading: isSettingApiKey }] = useSetClientApiKeyMutation();
  const [cycleSecret, { isLoading: isCyclingSecret }] = useCycleClientSecretMutation();
  const [deleteClient, { isLoading: isDeletingClient }] = useDeleteClientMutation();
  const [setClientSettings, { isLoading: isSettingClientSettings }] = useSetClientSettingsMutation();

  const isLoading = isSettingEndpoint || isSettingUsername || isSettingCustomerId || isSettingPassword ||
                   isSettingCertificate || isSettingApiKey || isCyclingSecret || isDeletingClient || isSettingClientSettings;
  
  // Determine AuthType based on selected provider
  const currentProvider = providers?.find(p => p.name === client?.providerName);
  const authType = currentProvider?.authType;

  // Initialize IP addresses when client data loads
  React.useEffect(() => {
    if (client?.clientIPs) {
      setIpAddresses(client.clientIPs);
    }
  }, [client]);

  const startEditing = (field: string, currentValue: string = '') => {
    setEditingFields(prev => ({ ...prev, [field]: true }));
    setEditValues(prev => ({ ...prev, [field]: currentValue }));
  };

  const cancelEditing = (field: string) => {
    setEditingFields(prev => ({ ...prev, [field]: false }));
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
  };

  const saveField = async (field: string) => {
    if (!client) return;
    
    try {
      const value = editValues[field];
      
      switch (field) {
        case 'endpoint':
          if (typeof value === 'string') {
            await setEndpoint({
              clientId: client.clientId,
              endpointName: value
            }).unwrap();
          }
          break;
          
        case 'username':
          if (typeof value === 'string') {
            await setUsername({ clientId: client.clientId, username: value }).unwrap();
          }
          break;

        case 'customerId':
          if (typeof value === 'string') {
            await setCustomerId({ clientId: client.clientId, customerId: value }).unwrap();
          }
          break;

        case 'password':
          if (typeof value === 'string') {
            await setPassword({ clientId: client.clientId, password: value }).unwrap();
          }
          break;
          
        case 'certificate':
          if (value instanceof File) {
            await setCertificate({ 
              clientId: client.clientId, 
              certificate: value,
              password: certificatePassword || undefined
            }).unwrap();
            // Add a small delay before refetching to allow backend to process
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          break;
          
        case 'apiKey':
          if (typeof value === 'string') {
            await setApiKey({ clientId: client.clientId, apiKey: value }).unwrap();
          }
          break;
      }
      
      setEditingFields(prev => ({ ...prev, [field]: false }));
      setEditValues(prev => {
        const newValues = { ...prev };
        delete newValues[field];
        return newValues;
      });
      
      // Special handling for certificate to clear the password field
      if (field === 'certificate') {
        setCertificatePassword('');
      }
      
      notify.success('Updated successfully', `${field} has been updated.`);

    } catch (error: any) {
      notify.error('Update failed', error?.data?.message || 'Please try again.');
    }
  };

  const handleCycleSecret = async () => {
    if (!client) return;
    
    try {
      const result = await cycleSecret(client.clientId).unwrap();
      
      // If secret is returned, show the secret modal and close this modal
      if (result?.secret) {
        setGeneratedSecret(result.secret);
        setShowSecretModal(true);
      } else {
        notify.success('Secret cycled', 'Client secret has been regenerated successfully.');
        refetchClient();
      }
    } catch (error: any) {
      notify.error('Failed to cycle secret', error?.data?.message || 'Please try again.');
    }
  };

  const handleSecretComplete = () => {
    setShowSecretModal(false);
    setGeneratedSecret(''); // Clear secret from memory for security
    notify.success('Secret cycled', 'Client secret has been regenerated successfully.');
    refetchClient();
    onClose(); // Close the edit modal and return to client details
  };

  const handleDeleteClient = async () => {
    if (!client) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete client "${client.clientId}"?\n\n` +
      'This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await deleteClient(client.clientId).unwrap();
      notify.success('Client deleted', 'The client has been deleted successfully.');
      // Call the deletion callback if provided (to close parent detail modal too)
      if (onClientDeleted) {
        onClientDeleted();
      } else {
        onClose();
      }
    } catch (error: any) {
      notify.error('Failed to delete client', error?.data?.message || 'Please try again.');
    }
  };

  const handleFileUpload = (file: File) => {
    if (file.name.toLowerCase().endsWith('.pfx')) {
      setEditValues(prev => ({ ...prev, certificate: file }));
    } else {
      notify.error('Invalid file type', 'Only .pfx files are allowed.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const validateIPv4OrCIDR = (input: string): boolean => {
    // Check if it's CIDR notation
    if (input.includes('/')) {
      const [ip, mask] = input.split('/');
      const ipPattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const maskNum = parseInt(mask, 10);
      return ipPattern.test(ip) && !isNaN(maskNum) && maskNum >= 0 && maskNum <= 32;
    }
    // Individual IP
    return validationPatterns.ipAddress.test(input);
  };

  // IP Whitelist handlers
  const handleAddIP = async () => {
    if (!client) return;

    setIpError('');

    const trimmedIP = newIPAddress.trim();

    if (!trimmedIP) {
      setIpError('Please enter an IP address or CIDR range');
      return;
    }

    // Validate IP address or CIDR format
    if (!validateIPv4OrCIDR(trimmedIP)) {
      setIpError('Invalid format. Examples: 192.168.1.1 or 192.168.1.0/24');
      return;
    }

    // Check for duplicates
    if (ipAddresses.includes(trimmedIP)) {
      setIpError('This IP address is already in the list');
      return;
    }

    const updatedIPs = [...ipAddresses, trimmedIP];

    try {
      await setClientSettings({
        clientId: client.clientId,
        clientIPs: updatedIPs
      }).unwrap();

      setIpAddresses(updatedIPs);
      setNewIPAddress('');
      notify.success('IP address added', 'IP address has been added to the whitelist.');
      refetchClient();
    } catch (error: any) {
      notify.error('Failed to add IP address', error?.data?.message || 'Please try again.');
    }
  };

  const handleRemoveIP = async (ipToRemove: string) => {
    if (!client) return;

    const updatedIPs = ipAddresses.filter(ip => ip !== ipToRemove);

    try {
      await setClientSettings({
        clientId: client.clientId,
        clientIPs: updatedIPs
      }).unwrap();

      setIpAddresses(updatedIPs);
      notify.success('IP address removed', 'IP address has been removed from the whitelist.');
      refetchClient();
    } catch (error: any) {
      notify.error('Failed to remove IP address', error?.data?.message || 'Please try again.');
    }
  };

  if (clientLoading || !client) {
    return (
      <Modal $isOpen>
        <ModalOverlay onClick={onClose} />
        <ModalContent>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Loading client details...
          </div>
        </ModalContent>
      </Modal>
    );
  }

  // No longer needed since provider and endpoint are the same

  // Show secret modal instead of edit modal when secret is being displayed
  if (showSecretModal) {
    return (
      <SecretDisplayModal
        isOpen={showSecretModal}
        secret={generatedSecret}
        title="New Client Secret Generated"
        clientId={client?.clientId || ''}
        secretType="client-secret"
        onComplete={handleSecretComplete}
      />
    );
  }

  return (
    <Modal $isOpen>
      <ModalOverlay onClick={onClose} />
      <ModalContent style={{ maxWidth: '800px' }}>
        <ModalHeader>
          <ModalTitle>Edit Client - {client.clientId}</ModalTitle>
          <Button variant="outline" size="sm" onClick={onClose}>
            <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
          </Button>
        </ModalHeader>
        
        <ModalBody>
              <EditSection>
                <SectionTitle>
                  <BuildingLibraryIcon />
                  Financial Institution
                </SectionTitle>
                
                <EditableFieldContainer>
                  <div style={{ flex: 1 }}>
                    <FieldLabel htmlFor="endpoint">Institution</FieldLabel>
                    {editingFields.endpoint ? (
                      <Select
                        id="endpoint"
                        value={editValues.endpoint as string || ''}
                        onChange={(e) => setEditValues(prev => ({ ...prev, endpoint: e.target.value }))}
                        style={{ 
                          height: '2.25rem',
                          padding: '0 3rem 0 0.75rem',
                          backgroundPosition: 'right 1rem center',
                          boxSizing: 'border-box',
                          fontSize: '0.875rem',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <option value="">Select financial institution</option>
                        {endpoints?.map(endpoint => (
                          <option key={endpoint} value={endpoint}>
                            {endpoint}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <DisplayField style={{ cursor: 'pointer' }} onClick={() => startEditing('endpoint', client.providerName || client.endpointName || '')}>
                        {client.providerName || client.endpointName || 'No institution set - click to select'}
                      </DisplayField>
                    )}
                  </div>
                  {editingFields.endpoint ? (
                    <EditControls>
                      <EditButton
                        onClick={() => saveField('endpoint')}
                        disabled={isLoading}
                      >
                        <CheckIcon style={{ width: '1rem', height: '1rem' }} />
                      </EditButton>
                      <EditButton
                        variant="outline"
                        onClick={() => cancelEditing('endpoint')}
                        disabled={isLoading}
                      >
                        <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                      </EditButton>
                    </EditControls>
                  ) : (
                    <EditControls>
                      <EditButton
                        variant="outline"
                        onClick={() => startEditing('endpoint', client.providerName || client.endpointName || '')}
                      >
                        <PencilIcon style={{ width: '1rem', height: '1rem' }} />
                      </EditButton>
                    </EditControls>
                  )}
                </EditableFieldContainer>
              </EditSection>

              {/* Credentials Section - for Banks and Payment Processors */}
              {(authType === AuthType.UsernamePasswordCertificate || authType === AuthType.UsernamePassword) && (
                <EditSection>
                  <SectionTitle>
                    <UserIcon />
                    Credentials
                  </SectionTitle>
                  
                  <FieldContainer>
                    <FieldLabel htmlFor="username">Username</FieldLabel>
                    <DisplayField>{client.username || 'Not set'}</DisplayField>
                  </FieldContainer>

                <EditableFieldContainer>
                  <div style={{ flex: 1 }}>
                    <FieldLabel htmlFor="password">Password</FieldLabel>
                    {editingFields.password ? (
                      <Input
                        id="password"
                        type="password"
                        value={editValues.password as string || ''}
                        onChange={(e) => setEditValues(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Bank connection password"
                        style={{
                          height: '2.25rem',
                          padding: '0 0.75rem',
                          boxSizing: 'border-box',
                          fontSize: '0.875rem'
                        }}
                      />
                    ) : (
                      <ClickableField onClick={() => startEditing('password')}>
                        Click to set new password
                      </ClickableField>
                    )}
                  </div>
                  {editingFields.password && (
                    <EditControls>
                      <EditButton
                        onClick={() => saveField('password')}
                        disabled={isLoading}
                      >
                        <CheckIcon style={{ width: '1rem', height: '1rem' }} />
                      </EditButton>
                      <EditButton
                        variant="outline"
                        onClick={() => cancelEditing('password')}
                        disabled={isLoading}
                      >
                        <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                      </EditButton>
                    </EditControls>
                  )}
                </EditableFieldContainer>
                </EditSection>
              )}

              {/* Credentials Section - for Danske Bank (CustomerId + signature) */}
              {authType === AuthType.CustomerIdSignature && (
                <EditSection>
                  <SectionTitle>
                    <UserIcon />
                    Credentials
                  </SectionTitle>

                  <EditableFieldContainer>
                    <div style={{ flex: 1 }}>
                      <FieldLabel htmlFor="customerId">Customer ID</FieldLabel>
                      {editingFields.customerId ? (
                        <Input
                          id="customerId"
                          type="text"
                          value={editValues.customerId as string || ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, customerId: e.target.value }))}
                          placeholder="Danske Bank Customer ID"
                          style={{
                            height: '2.25rem',
                            padding: '0 0.75rem',
                            boxSizing: 'border-box',
                            fontSize: '0.875rem'
                          }}
                        />
                      ) : (
                        <ClickableField onClick={() => startEditing('customerId', client.customerId || '')}>
                          {client.customerId || 'Click to set Customer ID'}
                        </ClickableField>
                      )}
                    </div>
                    {editingFields.customerId && (
                      <EditControls>
                        <EditButton
                          onClick={() => saveField('customerId')}
                          disabled={isLoading}
                        >
                          <CheckIcon style={{ width: '1rem', height: '1rem' }} />
                        </EditButton>
                        <EditButton
                          variant="outline"
                          onClick={() => cancelEditing('customerId')}
                          disabled={isLoading}
                        >
                          <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                        </EditButton>
                      </EditControls>
                    )}
                  </EditableFieldContainer>
                </EditSection>
              )}

              {/* API Configuration - for Card Processors */}
              {authType === AuthType.ApiKeyKeypair && (
                <EditSection>
                  <SectionTitle>
                    <CreditCardIcon />
                    API Configuration
                  </SectionTitle>
                  
                  <EditableFieldContainer>
                    <div style={{ flex: 1 }}>
                      <FieldLabel htmlFor="apiKey">API Key</FieldLabel>
                      {editingFields.apiKey ? (
                        <Input
                          id="apiKey"
                          type="password"
                          value={editValues.apiKey as string || ''}
                          onChange={(e) => setEditValues(prev => ({ ...prev, apiKey: e.target.value }))}
                          placeholder="Enter API key"
                          style={{
                            height: '2.25rem',
                            padding: '0 0.75rem',
                            boxSizing: 'border-box',
                            fontSize: '0.875rem'
                          }}
                        />
                      ) : (
                        <ClickableField onClick={() => startEditing('apiKey')}>
                          Click to set new API key
                        </ClickableField>
                      )}
                    </div>
                    {editingFields.apiKey && (
                      <EditControls>
                        <EditButton
                          onClick={() => saveField('apiKey')}
                          disabled={isLoading}
                        >
                          <CheckIcon style={{ width: '1rem', height: '1rem' }} />
                        </EditButton>
                        <EditButton
                          variant="outline"
                          onClick={() => cancelEditing('apiKey')}
                          disabled={isLoading}
                        >
                          <XCircleIcon style={{ width: '1rem', height: '1rem' }} />
                        </EditButton>
                      </EditControls>
                    )}
                  </EditableFieldContainer>
                </EditSection>
              )}
          
            {/* Certificate Management - for Banks only, hidden when org uses system certificate */}
           
            {authType === AuthType.UsernamePasswordCertificate && !orgsLoading && !organization?.useSystemCertificate && (
              <EditSection>
                <SectionTitle>
                  <DocumentArrowUpIcon />
                  Certificate Management
                </SectionTitle>

                <FieldContainer>
                  <FieldLabel>Current Certificate</FieldLabel>
                  <DisplayField>
                    {client.certificateName
                      ? client.certificateName
                      : client.certificateSerialNumber
                        ? `Certificate (Serial: ${client.certificateSerialNumber})`
                        : 'No certificate uploaded'}
                  </DisplayField>
                </FieldContainer>

                <FieldLabel>Upload New Certificate (.pfx)</FieldLabel>
                <FileUploadArea
                  $isDragOver={isDragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('certificate-file')?.click()}
                >
                  <DocumentArrowUpIcon style={{ width: '2rem', height: '2rem', margin: '0 auto 0.5rem' }} />
                  {editValues.certificate instanceof File ? (
                    <div>
                      <p><strong>{editValues.certificate.name}</strong></p>
                      <p>Click to replace</p>
                    </div>
                  ) : (
                    <div>
                      <p>Drag and drop your .pfx certificate file here</p>
                      <p>or click to browse</p>
                    </div>
                  )}
                </FileUploadArea>
                <FileInput
                  id="certificate-file"
                  type="file"
                  accept=".pfx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                  }}
                />

                {editValues.certificate instanceof File && (
                  <>
                    <FieldContainer style={{ marginTop: '1rem' }}>
                      <FieldLabel htmlFor="certificatePassword">Certificate Password (Optional)</FieldLabel>
                      <Input
                        id="certificatePassword"
                        type="password"
                        value={certificatePassword}
                        onChange={(e) => setCertificatePassword(e.target.value)}
                        placeholder="Enter certificate password if required"
                      />
                    </FieldContainer>

                    <ActionButtons>
                      <Button
                        onClick={() => saveField('certificate')}
                        disabled={isLoading}
                      >
                        Upload Certificate
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditValues(prev => {
                            const newValues = { ...prev };
                            delete newValues.certificate;
                            return newValues;
                          });
                          setCertificatePassword('');
                        }}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                    </ActionButtons>
                  </>
                )}
              </EditSection>
            )}
            
            {/* Key Pair Information - for Card Processors */}
            {authType === AuthType.ApiKeyKeypair && (
              <EditSection>
                <SectionTitle>
                  <ShieldCheckIcon />
                  Key Pair
                </SectionTitle>
                
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f3f4f6', 
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  <p style={{ margin: 0, color: '#374151', fontSize: '0.875rem' }}>
                    RSA key pair was generated during client creation and is stored securely in Azure Key Vault.
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                    The private key cannot be retrieved or regenerated.
                  </p>
                </div>
              </EditSection>
            )}

            {/* IP Whitelist Section - for support users only */}
            {isSupport && (
              <div style={{ marginBottom: '1.5rem' }}>
                <CollapsibleHeader
                  $isExpanded={isIPSectionExpanded}
                  onClick={() => setIsIPSectionExpanded(!isIPSectionExpanded)}
                >
                <CollapsibleTitle>
                  <ServerIcon />
                  <h3>IP Whitelisting</h3>
                </CollapsibleTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <StatusBadge $variant={ipAddresses.length === 0 ? 'disabled' : 'active'}>
                    {ipAddresses.length === 0
                      ? 'Disabled'
                      : `${ipAddresses.length} IP${ipAddresses.length === 1 ? '' : 's'} whitelisted`}
                  </StatusBadge>
                  <ChevronIcon>
                    {isIPSectionExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  </ChevronIcon>
                </div>
              </CollapsibleHeader>

              {isIPSectionExpanded && (
                <CollapsibleContent>
                  {ipAddresses.length === 0 ? (
                    <EmptyState>
                      No IP restrictions configured. All IP addresses are allowed.
                    </EmptyState>
                  ) : (
                    <IPListContainer>
                      {ipAddresses.map((ip, index) => (
                        <IPItem key={index}>
                          <span>{ip}</span>
                          <RemoveButton
                            onClick={() => handleRemoveIP(ip)}
                            disabled={isLoading}
                            title="Remove IP address"
                          >
                            <XCircleIcon />
                          </RemoveButton>
                        </IPItem>
                      ))}
                    </IPListContainer>
                  )}

                  <div>
                    <Label htmlFor="newIPAddress">Add IP Address</Label>
                    <AddIPContainer>
                      <div style={{ flex: 1 }}>
                        <Input
                          id="newIPAddress"
                          value={newIPAddress}
                          onChange={(e) => {
                            setNewIPAddress(e.target.value);
                            setIpError('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddIP();
                            }
                          }}
                          placeholder="Enter IP or CIDR range (e.g., 192.168.1.1 or 192.168.1.0/24)"
                          disabled={isLoading}
                        />
                      </div>
                      <Button
                        onClick={handleAddIP}
                        disabled={isLoading || !newIPAddress.trim()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          minWidth: 'auto'
                        }}
                      >
                        <PlusIcon style={{ width: '1rem', height: '1rem' }} />
                        Add
                      </Button>
                      
                    </AddIPContainer>
                    {ipError && <ErrorText>{ipError}</ErrorText>}
                  </div>

                  <Alert variant="info" style={{ marginTop: '1rem' }}>
                    When IP whitelisting is enabled (one or more IPs configured), only requests from these IP addresses will be allowed.
                    Remove all IPs to disable whitelisting and allow all addresses.
                  </Alert>
                </CollapsibleContent>
              )}
              </div>
            )}

            {/* Security Settings - for support users only */}
            {isSupport && (
              <EditSection>
                <SectionTitle>
                  <KeyIcon />
                  Security Settings
                </SectionTitle>

                <div style={{ marginBottom: '1rem' }}>
                  <Label>Client Secret</Label>
                  <div style={{
                    padding: '0.5rem',
                    background: '#f9fafb',
                    borderRadius: '0.375rem'
                  }}>
                    ••••••••••••••••••••••••••••••••
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Last updated: {client.modified ? new Date(client.modified).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>

                <Alert variant="warning" style={{ marginBottom: '1rem' }}>
                  Cycling the client secret will generate a new secret and invalidate the current one.
                  Make sure to update any applications using this client.
                </Alert>

                <Button
                  variant="outline"
                  onClick={handleCycleSecret}
                  disabled={isLoading}
                >
                  <KeyIcon style={{ width: '1rem', height: '1rem' }} />
                  {isCyclingSecret ? 'Cycling Secret...' : 'Cycle Client Secret'}
                </Button>
              </EditSection>
            )}

            {/* Danger Zone - Delete Client - for support users only */}
            {isSupport && (
              <EditSection style={{
                borderColor: '#fca5a5',
                backgroundColor: '#fef2f2'
              }}>
                <SectionTitle style={{ color: '#dc2626' }}>
                  <TrashIcon />
                  Danger Zone
                </SectionTitle>

                <Alert variant="error" style={{ marginBottom: '1rem' }}>
                  <strong>Warning:</strong> Deleting this client will set its status to Deleted,
                  immediately prevent all API access, and hide it from the client list. This action cannot be undone.
                </Alert>

                <Button
                  onClick={handleDeleteClient}
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <TrashIcon style={{ width: '1rem', height: '1rem' }} />
                  {isDeletingClient ? 'Deleting Client...' : 'Delete Client'}
                </Button>
              </EditSection>
            )}
        </ModalBody>
        
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}